import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQuery } from "@tanstack/react-query"
import { useCallback } from "react"
import { api } from "../../../api/client"
import type { Scope } from "../../../types"
import { scopeDisplayName } from "../../config/scopeIcons"

// Structural scope list — matches scope_config.py; always shown even before any import.
const STRUCTURAL_SCOPE_IDS = [
  "supplier",
  "substance",
  "component",
  "product",
  "customer",
]

/**
 * Full details of every structural scope: one cache entry, shared by the
 * configuration Aktionen tab and AP settings. A scope not yet in the DB is
 * cached as `null` and turned into an empty shell by `select`, so the shell's
 * translated name follows the current language instead of the one it was
 * first fetched in.
 */
export function useFullScopes() {
  const { translate } = useTranslation()

  const withShells = useCallback(
    (scopes: (Scope | null)[]): Scope[] =>
      scopes.map(
        (scope, idx) =>
          scope ?? {
            id: STRUCTURAL_SCOPE_IDS[idx],
            name: scopeDisplayName(STRUCTURAL_SCOPE_IDS[idx], translate),
            description: null,
            is_singleton: false,
            is_hidden: false,
            display_order: idx,
            primitives: [],
          },
      ),
    [translate],
  )

  return useQuery({
    queryKey: ["scopes-full-details"],
    queryFn: async () => {
      const items = await api.listScopes()
      const inDb = new Set(items.map((s) => s.id))
      return Promise.all(
        STRUCTURAL_SCOPE_IDS.map((id) =>
          inDb.has(id) ? api.getScope(id) : null,
        ),
      )
    },
    select: withShells,
    staleTime: 30_000,
  })
}
